// Soov /car-data and MNT return the same small set of Estonian vehicle enums.
// Keep the raw API response in the cache and translate only at render time so
// a language switch is instant and never needs a new request.

import { localizeUnitText } from './units';

export type VehicleLanguage = 'en' | 'et' | 'ru' | 'lv' | 'zh' | 'th';

export interface VehicleTranslationRow {
  et: string;
  en: string;
  ru: string;
  lv: string;
  zh: string;
  th: string;
}

export function vehicleLabel(row: VehicleTranslationRow, lang: string): string {
  return row[lang as VehicleLanguage] ?? row.en;
}

type Row = VehicleTranslationRow;

const COLORS: Record<string, Row> = {
  valge: { et: 'Valge', en: 'White', ru: 'Белый', lv: 'Balta', zh: '白色', th: 'สีขาว' },
  must: { et: 'Must', en: 'Black', ru: 'Чёрный', lv: 'Melna', zh: '黑色', th: 'สีดำ' },
  punane: { et: 'Punane', en: 'Red', ru: 'Красный', lv: 'Sarkana', zh: '红色', th: 'สีแดง' },
  roheline: { et: 'Roheline', en: 'Green', ru: 'Зелёный', lv: 'Zaļa', zh: '绿色', th: 'สีเขียว' },
  sinine: { et: 'Sinine', en: 'Blue', ru: 'Синий', lv: 'Zila', zh: '蓝色', th: 'สีน้ำเงิน' },
  hall: { et: 'Hall', en: 'Grey', ru: 'Серый', lv: 'Pelēka', zh: '灰色', th: 'สีเทา' },
  hobedane: { et: 'Hõbedane', en: 'Silver', ru: 'Серебристый', lv: 'Sudraba', zh: '银色', th: 'สีเงิน' },
  hbedane: { et: 'Hõbedane', en: 'Silver', ru: 'Серебристый', lv: 'Sudraba', zh: '银色', th: 'สีเงิน' },
  pruun: { et: 'Pruun', en: 'Brown', ru: 'Коричневый', lv: 'Brūna', zh: '棕色', th: 'สีน้ำตาล' },
  kollane: { et: 'Kollane', en: 'Yellow', ru: 'Жёлтый', lv: 'Dzeltena', zh: '黄色', th: 'สีเหลือง' },
  oranz: { et: 'Oranž', en: 'Orange', ru: 'Оранжевый', lv: 'Oranža', zh: '橙色', th: 'สีส้ม' },
  oran: { et: 'Oranž', en: 'Orange', ru: 'Оранжевый', lv: 'Oranža', zh: '橙色', th: 'สีส้ม' },
  lilla: { et: 'Lilla', en: 'Purple', ru: 'Фиолетовый', lv: 'Violeta', zh: '紫色', th: 'สีม่วง' },
  beez: { et: 'Beež', en: 'Beige', ru: 'Бежевый', lv: 'Bēša', zh: '米色', th: 'สีเบจ' },
  bee: { et: 'Beež', en: 'Beige', ru: 'Бежевый', lv: 'Bēša', zh: '米色', th: 'สีเบจ' },
  kuldne: { et: 'Kuldne', en: 'Gold', ru: 'Золотистый', lv: 'Zelta', zh: '金色', th: 'สีทอง' },
  roosa: { et: 'Roosa', en: 'Pink', ru: 'Розовый', lv: 'Rozā', zh: '粉色', th: 'สีชมพู' },
  tumehall: { et: 'Tumehall', en: 'Dark grey', ru: 'Тёмно-серый', lv: 'Tumši pelēka', zh: '深灰色', th: 'สีเทาเข้ม' },
  tumesinine: { et: 'Tumesinine', en: 'Dark blue', ru: 'Тёмно-синий', lv: 'Tumši zila', zh: '深蓝色', th: 'สีน้ำเงินเข้ม' },
  helehall: { et: 'Helehall', en: 'Light grey', ru: 'Светло-серый', lv: 'Gaiši pelēka', zh: '浅灰色', th: 'สีเทาอ่อน' },
  helebeez: { et: 'Helebeež', en: 'Light beige', ru: 'Светло-бежевый', lv: 'Gaiši bēša', zh: '浅米色', th: 'สีเบจอ่อน' },
  helebee: { et: 'Helebeež', en: 'Light beige', ru: 'Светло-бежевый', lv: 'Gaiši bēša', zh: '浅米色', th: 'สีเบจอ่อน' },
  tumeroheline: { et: 'Tumeroheline', en: 'Dark green', ru: 'Тёмно-зелёный', lv: 'Tumši zaļa', zh: '深绿色', th: 'สีเขียวเข้ม' },
  helesinine: { et: 'Helesinine', en: 'Light blue', ru: 'Светло-синий', lv: 'Gaiši zila', zh: '浅蓝色', th: 'สีฟ้าอ่อน' },
  tumepunane: { et: 'Tumepunane', en: 'Dark red', ru: 'Тёмно-красный', lv: 'Tumši sarkana', zh: '深红色', th: 'สีแดงเข้ม' },
  helepruun: { et: 'Helepruun', en: 'Light brown', ru: 'Светло-коричневый', lv: 'Gaiši brūna', zh: '浅棕色', th: 'สีน้ำตาลอ่อน' },
  tumepruun: { et: 'Tumepruun', en: 'Dark brown', ru: 'Тёмно-коричневый', lv: 'Tumši brūna', zh: '深棕色', th: 'สีน้ำตาลเข้ม' },
};

const FUEL_TYPES: Record<string, Row> = {
  bensiin: { et: 'Bensiin', en: 'Petrol', ru: 'Бензин', lv: 'Benzīns', zh: '汽油', th: 'เบนซิน' },
  mootoribensiin: { et: 'Mootoribensiin', en: 'Petrol', ru: 'Бензин', lv: 'Benzīns', zh: '汽油', th: 'เบนซิน' },
  diisel: { et: 'Diisel', en: 'Diesel', ru: 'Дизель', lv: 'Dīzeļdegviela', zh: '柴油', th: 'ดีเซล' },
  diislikutus: { et: 'Diislikütus', en: 'Diesel', ru: 'Дизель', lv: 'Dīzeļdegviela', zh: '柴油', th: 'ดีเซล' },
  diisliktus: { et: 'Diislikütus', en: 'Diesel', ru: 'Дизель', lv: 'Dīzeļdegviela', zh: '柴油', th: 'ดีเซล' },
  elekter: { et: 'Elekter', en: 'Electric', ru: 'Электричество', lv: 'Elektrība', zh: '电力', th: 'ไฟฟ้า' },
  hybriid: { et: 'Hübriid', en: 'Hybrid', ru: 'Гибрид', lv: 'Hibrīds', zh: '混合动力', th: 'ไฮบริด' },
  cng: { et: 'CNG', en: 'CNG', ru: 'КПГ (метан)', lv: 'CNG', zh: '压缩天然气（CNG）', th: 'ก๊าซธรรมชาติ CNG' },
  lpg: { et: 'LPG', en: 'LPG', ru: 'СУГ (газ)', lv: 'LPG', zh: '液化石油气（LPG）', th: 'ก๊าซ LPG' },
  'mootoribensiin / veeldatud naftagaas (lpg)': { et: 'Mootoribensiin / veeldatud naftagaas (LPG)', en: 'Petrol / LPG', ru: 'Бензин / СУГ (LPG)', lv: 'Benzīns / LPG', zh: '汽油 / 液化石油气（LPG）', th: 'เบนซิน / ก๊าซ LPG' },
  'mootoribensiin / elekter': { et: 'Mootoribensiin / elekter', en: 'Petrol / electric', ru: 'Бензин / электричество', lv: 'Benzīns / elektrība', zh: '汽油 / 电力', th: 'เบนซิน / ไฟฟ้า' },
  'diislikutus / elekter': { et: 'Diislikütus / elekter', en: 'Diesel / electric', ru: 'Дизель / электричество', lv: 'Dīzeļdegviela / elektrība', zh: '柴油 / 电力', th: 'ดีเซล / ไฟฟ้า' },
  'diisliktus / elekter': { et: 'Diislikütus / elekter', en: 'Diesel / electric', ru: 'Дизель / электричество', lv: 'Dīzeļdegviela / elektrība', zh: '柴油 / 电力', th: 'ดีเซล / ไฟฟ้า' },
  'mootoribensiin / maagaas / biometaan (cng)': { et: 'Mootoribensiin / maagaas / biometaan (CNG)', en: 'Petrol / natural gas / biomethane (CNG)', ru: 'Бензин / природный газ / биометан (CNG)', lv: 'Benzīns / dabasgāze / biometāns (CNG)', zh: '汽油 / 天然气 / 生物甲烷（CNG）', th: 'เบนซิน / ก๊าซธรรมชาติ / ไบโอมีเทน (CNG)' },
  'maagaas / biometaan (cng)': { et: 'Maagaas / biometaan (CNG)', en: 'Natural gas / biomethane (CNG)', ru: 'Природный газ / биометан (CNG)', lv: 'Dabasgāze / biometāns (CNG)', zh: '天然气 / 生物甲烷（CNG）', th: 'ก๊าซธรรมชาติ / ไบโอมีเทน (CNG)' },
};

const TRANSMISSIONS: Record<string, Row> = {
  automaat: { et: 'Automaat', en: 'Automatic', ru: 'Автоматическая', lv: 'Automātiskā', zh: '自动变速箱', th: 'เกียร์อัตโนมัติ' },
  manuaal: { et: 'Manuaal', en: 'Manual', ru: 'Механическая', lv: 'Manuālā', zh: '手动变速箱', th: 'เกียร์ธรรมดา' },
  kasikaigukast: { et: 'Käsikäigukast', en: 'Manual', ru: 'Механическая', lv: 'Manuālā', zh: '手动变速箱', th: 'เกียร์ธรรมดา' },
  poolautomaat: { et: 'Poolautomaat', en: 'Semi-automatic', ru: 'Полуавтоматическая', lv: 'Pusautomātiskā', zh: '半自动变速箱', th: 'เกียร์กึ่งอัตโนมัติ' },
  cvt: { et: 'CVT', en: 'CVT', ru: 'Вариатор', lv: 'CVT', zh: '无级变速箱（CVT）', th: 'เกียร์ CVT' },
  variaator: { et: 'Variaator', en: 'CVT', ru: 'Вариатор', lv: 'CVT', zh: '无级变速箱（CVT）', th: 'เกียร์ CVT' },
  dct: { et: 'DCT', en: 'Dual-clutch (DCT)', ru: 'Робот (DCT)', lv: 'Divsajūgu (DCT)', zh: '双离合变速箱（DCT）', th: 'เกียร์คลัตช์คู่ (DCT)' },
  'konstantne ulekanne': { et: 'Konstantne ülekanne', en: 'Fixed-ratio transmission', ru: 'Редуктор с постоянным передаточным числом', lv: 'Pastāvīga pārnesuma transmisija', zh: '固定速比传动', th: 'ระบบส่งกำลังอัตราทดคงที่' },
  'konstantne lekanne': { et: 'Konstantne ülekanne', en: 'Fixed-ratio transmission', ru: 'Редуктор с постоянным передаточным числом', lv: 'Pastāvīga pārnesuma transmisija', zh: '固定速比传动', th: 'ระบบส่งกำลังอัตราทดคงที่' },
};

const BODY_TYPES: Record<string, Row> = {
  sedaan: { et: 'Sedaan', en: 'Sedan', ru: 'Седан', lv: 'Sedans', zh: '轿车', th: 'ซีดาน' },
  universaal: { et: 'Universaal', en: 'Estate / wagon', ru: 'Универсал', lv: 'Universālis', zh: '旅行车', th: 'สเตชันแวกอน' },
  luukpara: { et: 'Luukpära', en: 'Hatchback', ru: 'Хэтчбек', lv: 'Hečbeks', zh: '掀背车', th: 'แฮทช์แบ็ก' },
  luukpra: { et: 'Luukpära', en: 'Hatchback', ru: 'Хэтчбек', lv: 'Hečbeks', zh: '掀背车', th: 'แฮทช์แบ็ก' },
  kupee: { et: 'Kupee', en: 'Coupé', ru: 'Купе', lv: 'Kupeja', zh: '双门轿跑车', th: 'คูเป้' },
  kabriolett: { et: 'Kabriolett', en: 'Convertible', ru: 'Кабриолет', lv: 'Kabriolets', zh: '敞篷车', th: 'รถเปิดประทุน' },
  lahtine: { et: 'Lahtine', en: 'Convertible / open body', ru: 'Кабриолет / открытый кузов', lv: 'Kabriolets / atvērta virsbūve', zh: '敞篷 / 开放式车身', th: 'รถเปิดประทุน / ตัวถังเปิด' },
  mahtuniversaal: { et: 'Mahtuniversaal', en: 'MPV', ru: 'Минивэн', lv: 'Minivens', zh: '多用途乘用车（MPV）', th: 'รถอเนกประสงค์ MPV' },
  maastur: { et: 'Maastur', en: 'SUV / off-roader', ru: 'Внедорожник', lv: 'Apvidus auto', zh: 'SUV / 越野车', th: 'เอสยูวี / รถออฟโรด' },
  linnamaastur: { et: 'Linnamaastur', en: 'Crossover SUV', ru: 'Кроссовер', lv: 'Krosovers', zh: '跨界 SUV', th: 'ครอสโอเวอร์ SUV' },
  pikap: { et: 'Pikap', en: 'Pick-up', ru: 'Пикап', lv: 'Pikaps', zh: '皮卡', th: 'รถกระบะ' },
  furgoon: { et: 'Furgoon', en: 'Van', ru: 'Фургон', lv: 'Furgons', zh: '厢式货车', th: 'รถตู้บรรทุก' },
  kaubik: { et: 'Kaubik', en: 'Van', ru: 'Фургон', lv: 'Furgons', zh: '厢式货车', th: 'รถตู้บรรทุก' },
  buss: { et: 'Buss', en: 'Bus', ru: 'Автобус', lv: 'Autobuss', zh: '客车', th: 'รถโดยสาร' },
  mootorratas: { et: 'Mootorratas', en: 'Motorcycle', ru: 'Мотоцикл', lv: 'Motocikls', zh: '摩托车', th: 'รถจักรยานยนต์' },
  'mitmeotstarbeline soiduk': { et: 'Mitmeotstarbeline sõiduk', en: 'Multipurpose vehicle', ru: 'Многоцелевое транспортное средство', lv: 'Daudzfunkcionāls transportlīdzeklis', zh: '多用途车辆', th: 'รถอเนกประสงค์' },
  'mitmeotstarbeline siduk': { et: 'Mitmeotstarbeline sõiduk', en: 'Multipurpose vehicle', ru: 'Многоцелевое транспортное средство', lv: 'Daudzfunkcionāls transportlīdzeklis', zh: '多用途车辆', th: 'รถอเนกประสงค์' },
  korrusbuss: { et: 'Korrusbuss', en: 'Double-decker bus', ru: 'Двухэтажный автобус', lv: 'Divstāvu autobuss', zh: '双层客车', th: 'รถโดยสารสองชั้น' },
};

export function normalizeVehicleKey(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/õ/g, 'o')
    .replace(/š/g, 's')
    .replace(/ž/g, 'z')
    .replace(/\s+/g, ' ');
}

function humanizeUnderscores(raw: string): string {
  return raw
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

export function translateColor(raw: string | null | undefined, lang: string): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const row = COLORS[normalizeVehicleKey(trimmed)];
  return row ? vehicleLabel(row, lang) : trimmed;
}

/** Fuel arrives as e.g. "Bensiin_katalysaator": translate the base before "_". */
export function translateFuelType(raw: string | null | undefined, lang: string): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const exact = FUEL_TYPES[normalizeVehicleKey(trimmed)];
  if (exact) return vehicleLabel(exact, lang);
  const base = trimmed.split('_')[0] ?? trimmed;
  const row = FUEL_TYPES[normalizeVehicleKey(base)];
  return row ? vehicleLabel(row, lang) : humanizeUnderscores(trimmed);
}

export function translateTransmission(raw: string | null | undefined, lang: string): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const row = TRANSMISSIONS[normalizeVehicleKey(trimmed)];
  return row ? vehicleLabel(row, lang) : humanizeUnderscores(trimmed);
}

export function translateBodyType(raw: string | null | undefined, lang: string): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const row = BODY_TYPES[normalizeVehicleKey(trimmed)];
  return row ? vehicleLabel(row, lang) : humanizeUnderscores(trimmed);
}

/** Normalizes and localizes units embedded in Soov static values. */
export function formatStaticValue(
  raw: string | null | undefined,
  locale = 'et',
): string | null {
  return localizeUnitText(raw, locale);
}

export interface PriceRange {
  low: string;
  high: string | null;
}

/** Groups long digit runs by thousands ("2500 €" → "2 500 €"); already-spaced
 *  amounts ("26 700") pass through untouched. */
function groupThousands(text: string): string {
  return text.replace(/\d{4,}/g, (digits) => digits.replace(/\B(?=(\d{3})+$)/g, ' '));
}

/**
 * Splits an auto24 range like "12 000 kuni 15 000 €" into its two ends.
 * Tries the localized separator first, then the Estonian "kuni" the API uses.
 * Single prices (or unparseable text) come back as { low: text, high: null }.
 */
export function splitPriceRange(text: string | null | undefined, localizedSeparator: string): PriceRange | null {
  const trimmed = text?.trim();
  if (!trimmed) return null;
  for (const sep of [localizedSeparator.trim(), 'kuni']) {
    if (!sep) continue;
    const escaped = sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = trimmed.split(new RegExp(`\\s+${escaped}\\s+`, 'i'));
    if (parts.length === 2 && parts[0] && parts[1]) {
      return { low: groupThousands(parts[0]), high: groupThousands(parts[1]) };
    }
  }
  return { low: groupThousands(trimmed), high: null };
}
